import { useState } from 'react';
import { 
  LayoutDashboard, 
  AtSign, 
  User,
  LogOut,
  ChevronDown,
  CreditCard,
  Bell,
  Settings,
  HelpCircle,
  Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import LanguageSwitcher from '../LanguageSwitcher';
import PlanDetails from './PlanDetails';

interface DashboardLayoutProps {
  children: React.ReactNode;
  isPlanModalOpen: boolean;
  onPlanModalClose: () => void;
  onOpenPlanModal: () => void;
}

const DashboardLayout = ({ 
  children, 
  isPlanModalOpen, 
  onPlanModalClose,
  onOpenPlanModal 
}: DashboardLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { t } = useTranslation();

  const handleLogout = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const rootDomain = parts.length > 1 ? '.' + parts.slice(-2).join('.') : hostname;

    Cookies.remove('uumail_ut', { domain: rootDomain });
    Cookies.remove('uumail_ut');
    document.cookie = `uumail_ut=; path=/; domain=${rootDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`;
    document.cookie = 'uumail_ut=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure';
    window.location.href = '/';
  };

  const menuItems = [
    { icon: <LayoutDashboard size={20} />, label: t('nav.dashboard'), href: '/dashboard' },
    { icon: <AtSign size={20} />, label: t('nav.aliases'), href: '/dashboard/aliases' },
    { icon: <Activity size={20} />, label: t('nav.activities'), href: '/dashboard/activities' },
    { icon: <Settings size={20} />, label: t('nav.settings'), href: '/dashboard/settings' },
    { icon: <HelpCircle size={20} />, label: t('nav.help'), href: '/dashboard/help' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className={`bg-white w-64 border-r border-gray-200 flex-shrink-0 ${isSidebarOpen ? '' : 'hidden'} md:block`}>
        <div className="h-16 flex items-center px-6 border-b border-gray-200">
          <AtSign className="h-6 w-6 text-blue-600 mr-2" />
          <span className="text-xl font-bold text-blue-600">Uumail</span>
        </div>
        <nav className="p-4">
          {menuItems.map((item, index) => (
            <Link
              key={index}
              to={item.href}
              className="flex items-center px-4 py-3 text-gray-700 rounded-lg hover:bg-gray-100 mb-1"
            >
              {item.icon}
              <span className="ml-3">{item.label}</span>
            </Link>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="md:hidden mr-4"
            >
              <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-xl font-semibold text-gray-800">{t('dashboard.title')}</h1>
          </div>

          <div className="flex items-center space-x-4">
            <LanguageSwitcher />

            <button className="p-1.5 rounded-lg hover:bg-gray-100 relative">
              <Bell size={20} className="text-gray-600" />
              <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full"></span>
            </button>

            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100"
              >
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <User size={16} className="text-blue-600" />
                </div>
                <ChevronDown size={16} className="text-gray-600" />
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200">
                  <button 
                    onClick={() => {
                      onOpenPlanModal();
                      setIsProfileOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <CreditCard size={16} className="mr-2" />
                    {t('nav.myPlan', 'My Plan')}
                  </button>
                  <Link to="/dashboard/settings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    {t('nav.settings')}
                  </Link>
                  <hr className="my-1" />
                  <button 
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    <span className="flex items-center">
                      <LogOut size={16} className="mr-2" />
                      {t('auth.signOut')}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>

        <PlanDetails isOpen={isPlanModalOpen} onClose={onPlanModalClose} />
      </div>
    </div>
  );
};

export default DashboardLayout;